<template>
  <accordion-item-fi
    v-if="approvals"
    ref="accordionItem"
    :class="{
      'accordion-document-downloads-fi': true,
      'accordion-document-downloads-fi--no-hierarchy': !enableGroupedListView,
    }"
    header-tag="h3"
    ignore-others
    scroll-to
    @accordion-item-toggle="accordionToggled = !accordionToggled"
  >
    <template #header>
      {{ $dict.get('product-Catalog.Accordions.Document-Downloads.Label') }}
    </template>
    <template #content="{ isOpen }">
      <grid-columns-fi
        :columns="12"
        grid-class="lg:fi-grid-cols-10"
      >
        <div class="fi-col-span-12 lg:fi-col-span-10">
          <template v-if="enableCategoryTabs">
            <tab-navigation-fi
              :tab-navigation="tabNavigation"
              :refresh-tabs="accordionToggled"
              :line-hidden="true"
              :init-id="initTab"
            >
              <template
                v-for="item in tabNavigation"
                :key="item.id"
                #[getTabName(item.id)]
              >
                <div class="accordion-document-downloads-fi__approval-list">
                  <document-download-items-fi
                    :documents="item.documents"
                    :is-focusable="isOpen"
                    theme="category-tabs"
                  />
                </div>
              </template>
            </tab-navigation-fi>
          </template>
          <!-- XXX Remove after the product data and it's interface has been updated. Unwrap tab navigation above -->
          <template v-else>
            <div class="accordion-document-downloads-fi__approval-list">
              <document-download-items-fi
                :documents="approvals"
                :is-focusable="isOpen"
              />
            </div>
          </template>
        </div>
      </grid-columns-fi>
    </template>
  </accordion-item-fi>
</template>

<script setup lang="ts">
import { AccordionItemFi } from 'atoms';
import { computed, ref, useTemplateRef } from 'vue';

import DocumentDownloadItemsFi from '@/components/document-download/DocumentDownloadItemsFi.vue';
import GridColumnsFi from '@/components/grid-fi/GridColumnsFi.vue';
import TabNavigationFi from '@/components/tab-navigation-fi/TabNavigationFi.vue';
import { useFields } from '@/composables/fields';
import { useDict } from '@/composables/fischerPlugin';
import type { CheckboxField } from '@/sitecoreFieldTypes';

defineOptions({ name: 'AccordionDocumentDownloadsFi' });

type AccordionDocumentDownloadsFields = {
  enableDocumentTabs?: CheckboxField;
  enableGroupedListView?: CheckboxField;
};

const props = withDefaults(
  defineProps<{
    catalogData?: Record<string, any>;
    tabHash?: string | null;
  }>(),
  {
    catalogData: () => ({}),
    tabHash: null,
  },
);

const dict = useDict();
const fields = useFields<AccordionDocumentDownloadsFields>();

const accordionItem = useTemplateRef<{ toggleAccordion: () => void }>('accordionItem');

const accordionToggled = ref(false);
const initTab = ref(0);

const enableCategoryTabs = computed(() => fields?.enableDocumentTabs?.value);
const enableGroupedListView = computed(() => Boolean(fields.enableGroupedListView?.value));

const approvals = computed<any[] | null>(() => {
  if (!props.catalogData?.Documents) {
    return null;
  }

  let result: any[] = [];

  const catalogApprovals = props.catalogData.Documents.Approvals;
  const catalogDocuments = props.catalogData.Documents.AdditionalDocuments;

  if (catalogApprovals) {
    result.push(...catalogApprovals);
  }
  if (catalogDocuments) {
    result.push(...catalogDocuments);
  }

  if (!result.length) {
    return null;
  }

  if (enableGroupedListView.value) {
    const isChild = new Set();
    result = result.map((approval) => {
      const childIds = approval.ChildDocumentIds;

      if (childIds && childIds.length > 0) {
        const children: any[] = [];

        childIds.forEach((id: any) => {
          const childData = result.find((a) => a.DocumentId === id && a.ParentDocumentId);

          if (childData) {
            children.push(childData);
            isChild.add(id);
          }
        });

        const sortedChildren = children.sort(
          (a, b) => new Date(b.ValidFrom).getTime() - new Date(a.ValidFrom).getTime(),
        );

        return {
          ...approval,
          Subitems: sortedChildren,
        };
      }
      return approval;
    });

    return result.filter((a) => !isChild.has(a.DocumentId));
  }
  return result;
});

const tabNavigation = computed(() => {
  const groups: Record<string, any> = {};
  let id = 0;
  (approvals.value ?? []).forEach((catalogDocument: any) => {
    const group = catalogDocument.Group?.Value || dict.get('product-Catalog.No-Document-Group');

    if (groups[group]) {
      groups[group].documents.push(catalogDocument);
      return;
    }

    groups[group] = {
      id,
      text: group,
      documents: [catalogDocument],
      rank: catalogDocument.Group?.GroupRank,
      type: catalogDocument.Type,
    };
    id += 1;
  });
  const sortedGroups = Object.values(groups).sort((a: any, b: any) => a.rank - b.rank);
  sortedGroups.forEach((group: any) =>
    group.documents.sort((a: any, b: any) => a.DocumentRank - b.DocumentRank),
  );
  return sortedGroups;
});

function getTabName(id: number) {
  return `tab-content-${id}`;
}

function openAccordion() {
  if (props.tabHash) {
    let tab = null;
    if (props.tabHash === 'etas-and-dops') {
      tab = tabNavigation.value.find(
        (el: any) =>
          el.type === 'ETA - European Technical Assessment' ||
          el.type === 'DOP - Declaration of Performance',
      );
    } else if (props.tabHash === 'safety-data-sheets') {
      tab = tabNavigation.value.find((el: any) => el.type === 'Safety Datasheet');
    } else if (props.tabHash === 'sustainability') {
      tab = tabNavigation.value.find((el: any) => el.type === 'Environmental Product Declaration');
    }
    initTab.value = tab?.id || 0;
  }

  accordionItem.value?.toggleAccordion();
}

defineExpose({ openAccordion });
</script>

<style src="./accordion-document-downloads-fi-unscoped" />

<style scoped src="./accordion-document-downloads-fi" />
